import { useState, useCallback, useEffect, useRef } from 'react';
import type { Era } from '../game/types';
import { formatNumber } from '../game/utils';

interface Floater {
  id: number;
  x: number;
  y: number;
  value: number;
}

interface WorldOrbProps {
  era: Era;
  onClick: () => void;
  energyPerClick: number;
  clickCount: number;
}

export function WorldOrb({ era, onClick, energyPerClick, clickCount }: WorldOrbProps) {
  const [pressed, setPressed] = useState(false);
  const [floaters, setFloaters] = useState<Floater[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const floaterTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      floaterTimeoutsRef.current.forEach(clearTimeout);
      floaterTimeoutsRef.current.clear();
    },
    [],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick();
      setPressed(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setPressed(false), 120);

      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const id = Date.now() + Math.random();
      setFloaters((prev) => [...prev, { id, x, y, value: energyPerClick }]);
      const timer = setTimeout(() => {
        setFloaters((prev) => prev.filter((f) => f.id !== id));
        floaterTimeoutsRef.current.delete(timer);
      }, 800);
      floaterTimeoutsRef.current.add(timer);
    },
    [onClick, energyPerClick],
  );

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-white">{era.name}</h2>
        <p className="mt-1 max-w-xs text-sm text-gray-400">{era.description}</p>
      </div>

      <div className="relative">
        <button
          onClick={handleClick}
          className={`relative h-52 w-52 cursor-pointer select-none rounded-full shadow-2xl transition-transform duration-100 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/50 ${
            pressed ? 'scale-95' : 'scale-100 hover:scale-105 active:scale-95'
          }`}
          style={{
            background: era.orbGradient,
            boxShadow: `0 0 40px 8px ${era.glowColor}, 0 20px 60px rgba(0,0,0,0.5)`,
          }}
          aria-label={`Click to gather ${formatNumber(energyPerClick)} energy`}
        >
          <span className="text-6xl" role="img" aria-hidden="true">
            {era.icon}
          </span>
          <div className="absolute inset-0 rounded-full bg-white/0 transition-colors hover:bg-white/5" />
        </button>

        {floaters.map((f) => (
          <div
            key={f.id}
            className="pointer-events-none absolute font-bold text-yellow-300"
            style={{
              left: f.x,
              top: f.y,
              fontSize: '0.875rem',
              animation: 'floatUp 0.8s ease-out forwards',
              transform: 'translate(-50%, -50%)',
            }}
          >
            +{formatNumber(f.value)}
          </div>
        ))}
      </div>

      <div className="text-center">
        <p className="text-sm font-medium text-gray-300">
          +{formatNumber(energyPerClick)} energy per click
        </p>
        <p className="mt-0.5 text-xs text-gray-500">
          <span className="tabular-nums">{formatNumber(clickCount)}</span> total clicks
        </p>
      </div>
    </div>
  );
}
